import { useEffect, useRef } from "react";
import { Loader2, Sparkles } from "lucide-react";

const STARTERS_SONG = [
  "How should I arrange the choir on this song?",
  "Give me three ideas to make the chorus hit harder",
  "What tempo and key suit my voice on this song?",
  "How do I make my lead vocal sit on top of the mix?",
];
const STARTERS_GENERAL = [
  "Help me plan a new Easter worship song",
  "How do I record clean vocals at home?",
  "What makes a gospel vamp work?",
  "Explain call-and-response for a choir",
];

/** Plain text with **bold** emphasis (the only markdown the assistant uses). */
function RichText({ text }) {
  return text.split(/(\*\*[^*\n]+\*\*)/g).map((part, i) => (
    part.startsWith("**") && part.endsWith("**") && part.length > 4
      ? <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>
  ));
}

function Bubble({ m }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={`assistant-msg-${m.role}`}
        className={`max-w-[88%] whitespace-pre-wrap break-words rounded-2xl px-4 py-3 text-[14px] leading-relaxed ${
          mine ? "rounded-br-md bg-blue-500/25 text-white ring-1 ring-blue-400/30" : "rounded-bl-md bg-white/[0.05] text-white/90 ring-1 ring-white/10"
        }`}
      >
        {m.content ? <RichText text={m.content} /> : (m.pending ? <span className="inline-flex items-center gap-2 text-white/55"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…</span> : null)}
      </div>
    </div>
  );
}

/** Scrolling list of the conversation, with starter questions when it's empty. */
export default function AssistantThread({ messages, hasSong, onPick, disabled }) {
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [messages]);
  if (!messages.length) {
    return (
      <div data-testid="assistant-empty" className="flex flex-1 flex-col justify-end gap-3 px-1 pb-2">
        <p className="flex items-center gap-2 font-display text-[15px] font-semibold text-white/90"><Sparkles className="h-4 w-4 text-amber-300" /> Ask about arranging, singing or mixing</p>
        <div className="grid gap-2">
          {(hasSong ? STARTERS_SONG : STARTERS_GENERAL).map((q, i) => (
            <button key={q} type="button" data-testid={`assistant-starter-${i}`} disabled={disabled} onClick={() => onPick(q)}
              className="rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-left text-[13px] text-white/75 transition-colors duration-200 hover:border-amber-300/40 hover:bg-amber-500/10 hover:text-white disabled:opacity-40">
              {q}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div data-testid="assistant-thread" className="flex-1 space-y-3 overflow-y-auto px-1 pb-2">
      {messages.map((m) => <Bubble key={m.id} m={m} />)}
      <div ref={end} />
    </div>
  );
}
